import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Plus, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";

const DAYS = [
  { key: "0", label: "Lundi" },
  { key: "1", label: "Mardi" },
  { key: "2", label: "Mercredi" },
  { key: "3", label: "Jeudi" },
  { key: "4", label: "Vendredi" },
  { key: "5", label: "Samedi" },
  { key: "6", label: "Dimanche" },
];

const DEFAULT_WEEKLY = {
  "0": 7.2, "1": 7.2, "2": 7.2, "3": 7.2, "4": 7.2, "5": 0, "6": 0,
};

export const HoursScheduleDialog = ({ open, onOpenChange, employee, initialSchedule, onSaved }) => {
  const [enabled, setEnabled] = useState(false);
  const [weekly, setWeekly] = useState(DEFAULT_WEEKLY);
  const [overrides, setOverrides] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    const s = initialSchedule || {};
    setEnabled(!!s.enabled);
    setWeekly({ ...DEFAULT_WEEKLY, ...(s.weekly_hours || {}) });
    setOverrides(s.seasonal_overrides || []);
  }, [open, initialSchedule]);

  const setDay = (k, v) => {
    const n = v === "" ? 0 : parseFloat(v);
    setWeekly((w) => ({ ...w, [k]: isNaN(n) ? 0 : n }));
  };

  const addOverride = () => {
    setOverrides((o) => [
      ...o,
      { start: "", end: "", weekly_hours: { ...weekly } },
    ]);
  };

  const removeOverride = (idx) => setOverrides((o) => o.filter((_, i) => i !== idx));

  const setOverrideField = (idx, field, value) => {
    setOverrides((o) => o.map((ov, i) => (i === idx ? { ...ov, [field]: value } : ov)));
  };

  const setOverrideDay = (idx, k, v) => {
    const n = v === "" ? 0 : parseFloat(v);
    setOverrides((o) =>
      o.map((ov, i) =>
        i === idx
          ? { ...ov, weekly_hours: { ...(ov.weekly_hours || DEFAULT_WEEKLY), [k]: isNaN(n) ? 0 : n } }
          : ov
      )
    );
  };

  const weeklyTotal = Object.values(weekly).reduce((s, v) => s + (parseFloat(v) || 0), 0);

  const handleSave = async () => {
    // Validation overrides
    for (const [i, ov] of overrides.entries()) {
      if (!ov.start || !ov.end) {
        toast.error(`Période ${i + 1} : dates manquantes`);
        return;
      }
      if (ov.start > ov.end) {
        toast.error(`Période ${i + 1} : la date de début doit être avant la date de fin`);
        return;
      }
    }
    try {
      setSaving(true);
      const payload = {
        work_schedule: {
          enabled,
          weekly_hours: weekly,
          seasonal_overrides: overrides,
        },
      };
      await api.updateEmployee(employee.id, payload);
      toast.success("Horaires enregistrés");
      onSaved?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec enregistrement");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[640px] max-h-[85vh] overflow-y-auto" data-testid="schedule-dialog">
        <DialogHeader>
          <DialogTitle>Horaires contractuels — {employee.first_name} {employee.last_name}</DialogTitle>
          <DialogDescription>
            Sans activation, le système utilise le planning par défaut (7,2h hiver / 8,2h été sur les semaines 13-39).
            Activez pour personnaliser les heures par jour de la semaine et définir des périodes saisonnières.
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-md">
          <Switch
            id="schedule-enabled"
            checked={enabled}
            onCheckedChange={setEnabled}
            data-testid="schedule-enabled-toggle"
          />
          <Label htmlFor="schedule-enabled" className="cursor-pointer text-sm">
            Utiliser un planning personnalisé pour ce salarié
          </Label>
          <div className="flex-1" />
          <Badge variant="outline" className="font-mono text-xs">
            {weeklyTotal.toFixed(1)}h / semaine
          </Badge>
        </div>

        <div className={`space-y-3 ${!enabled ? "opacity-50 pointer-events-none" : ""}`}>
          <div>
            <Label className="text-sm font-medium">Planning hebdomadaire (heures par jour)</Label>
            <div className="grid grid-cols-7 gap-1.5 mt-2">
              {DAYS.map((d) => (
                <div key={d.key} className="text-center">
                  <div className="text-[10px] text-muted-foreground">{d.label}</div>
                  <Input
                    type="number"
                    step="0.1"
                    min="0"
                    max="24"
                    value={weekly[d.key] ?? 0}
                    onChange={(e) => setDay(d.key, e.target.value)}
                    className="h-9 text-center text-sm"
                    data-testid={`weekly-day-${d.key}`}
                  />
                </div>
              ))}
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between">
              <Label className="text-sm font-medium">Périodes saisonnières (optionnel)</Label>
              <Button size="sm" variant="outline" onClick={addOverride} data-testid="add-override-btn">
                <Plus className="h-3.5 w-3.5 mr-1" />Ajouter une période
              </Button>
            </div>
            <p className="text-[11px] text-muted-foreground">
              Ex: horaires d'été différents entre le 15/06 et 31/08.
            </p>
            <div className="space-y-3 mt-2">
              {overrides.length === 0 && (
                <p className="text-xs text-muted-foreground italic text-center py-2">Aucune période personnalisée</p>
              )}
              {overrides.map((ov, i) => (
                <div key={i} className="border rounded-md p-3 bg-white space-y-2">
                  <div className="flex items-center gap-2">
                    <Input
                      type="date"
                      value={ov.start || ""}
                      onChange={(e) => setOverrideField(i, "start", e.target.value)}
                      className="h-8"
                    />
                    <span className="text-xs text-muted-foreground">au</span>
                    <Input
                      type="date"
                      value={ov.end || ""}
                      onChange={(e) => setOverrideField(i, "end", e.target.value)}
                      className="h-8"
                    />
                    <Button size="sm" variant="ghost" onClick={() => removeOverride(i)} className="ml-auto">
                      <Trash2 className="h-3.5 w-3.5 text-red-500" />
                    </Button>
                  </div>
                  <div className="grid grid-cols-7 gap-1">
                    {DAYS.map((d) => (
                      <div key={d.key} className="text-center">
                        <div className="text-[9px] text-muted-foreground">{d.label.slice(0, 3)}</div>
                        <Input
                          type="number"
                          step="0.1"
                          min="0"
                          max="24"
                          value={ov.weekly_hours?.[d.key] ?? 0}
                          onChange={(e) => setOverrideDay(i, d.key, e.target.value)}
                          className="h-7 text-center text-[11px]"
                        />
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={saving}>Annuler</Button>
          <Button onClick={handleSave} disabled={saving} data-testid="save-schedule-btn">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Enregistrer"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default HoursScheduleDialog;
